import { Collapse } from 'antd';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import DropdownIcon from 'src/shared/assets/icons/triangle.svg';

import type { GeoLoadSection } from '../../entities/geological-load/geo-load-section.entity';
import type { GeoLoadStage } from '../../entities/geological-load/geo-load-stage.entity';
import type { GeologicalLoadValidation } from '../../entities/geological-load/geological-load-validation';
import type { RigOperationStage } from '../../entities/rig-operations/rig-operation-stage.entity';
import { GeologicalItem } from '../geological-load-item';
import { GeologicalLoadSection } from '../geological-load-section';
import type { GeologicalLoadViewModel } from '../geological-load-view-model';

import styles from './geological-load-stage.module.scss';

type Props = {
  geoLoadStage: GeoLoadStage;
  stage: RigOperationStage;
  viewModel: GeologicalLoadViewModel;
  validation: GeologicalLoadValidation;
  onValueChange(fieldId: string, value: unknown, geoLoadStage: GeoLoadStage, geoLoadSection?: GeoLoadSection): void;
};

export const GeologicalLoadStage = observer<Props>(function GeologicalLoadStage({
  geoLoadStage,
  stage,
  viewModel,
  validation,
  onValueChange,
}) {
  const { sections: geoLoadSections, geologicalLoad } = geoLoadStage;
  const { sections } = stage;

  const getSectionType = viewModel.getSectionTypeFn;

  const [activePanels, setActivePanels] = useState(() => {
    if (!geoLoadSections.length) {
      return [];
    }

    return geoLoadSections.map((section) => section.entityId.toString());
  });

  if (!geoLoadSections.length) {
    return (
      <GeologicalItem
        geologicalLoad={geologicalLoad}
        viewModel={viewModel}
        validation={validation}
        onValueChange={(fieldId, value) => onValueChange(fieldId, value, geoLoadStage)}
      />
    );
  }

  const handleChangeActivePanel = (keys: string | string[]) => {
    if (!Array.isArray(keys)) {
      setActivePanels([keys]);
      return;
    }

    setActivePanels(keys);
  };

  return (
    <div className={styles.wrapper}>
      <Collapse
        onChange={handleChangeActivePanel}
        activeKey={activePanels}
        expandIcon={({ isActive }) => {
          return (
            <div className={styles.expandIconContainer}>
              <DropdownIcon
                className={clsx(styles.expandIcon, isActive && styles.expandIconActive, styles.expandIconDisabled)}
              />
            </div>
          );
        }}
      >
        {geoLoadSections.map((geoLoadSection) => {
          const section = sections.find((section) => section.entityId === geoLoadSection.sectionEntityId);

          if (!section) {
            console.error('cannot find corresponding section', geoLoadSection);
            return null;
          }

          const sectionType = getSectionType(section);

          return (
            <Collapse.Panel key={geoLoadSection.entityId} className={styles.sectionPanel} header={sectionType}>
              <GeologicalLoadSection
                geoLoadSection={geoLoadSection}
                viewModel={viewModel}
                validation={validation}
                onValueChange={(fieldId, value, section) => onValueChange(fieldId, value, geoLoadStage, section)}
              />
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </div>
  );
});
